/** @import {GetServerSideAuthProps} from './utils' */

import { signIn as authenticate } from 'next-auth/react'
import { useRouter } from 'next/router.js'
import { useEffect, useState } from 'react'
import Head from 'next/head'
import { Title as DialogTitle } from '../../components/dialog'
import { Circular as Loader } from '../../components/loader'
import { Flex } from '@radix-ui/themes'
import { parseBody } from 'next/dist/server/api-utils/node/parse-body'
import { withSecret } from '../server-side-secrets'
import { Actions } from './utils'

export { CallbackPage as Page, getServerSideProps, routeParams }

const routeParams = '[ssoType]/[organizationId]'

/**
 * @param {{
 *   query: {
 *     organizationId: string
 *     callbackUrl: string
 *   } & (
 *     | {
 *         ssoType: 'oidc'
 *         code?: string
 *         state?: string
 *         cookie?: string
 *         redirectURL?: string
 *       }
 *     | {
 *         ssoType: 'saml'
 *         relayState: string
 *         samlResponse: string
 *       }
 *   )
 *   routes: {
 *     signIn: string
 *   }
 *   params: string[]
 * }} props
 */
function CallbackPage({
  organizationId,
  ssoType,
  callbackParams,
  query,
  routes,
}) {
  const { callbackUrl } = query
  const router = useRouter()
  const [status, setStatus] = useState('loading')

  useEffect(() => {
    handleCallback()

    async function handleCallback() {
      const signIn = await authenticate(`${ssoType}-callback`, {
        redirect: false,
        organizationId,
        ...callbackParams,
      })

      const destination = signIn.ok
        ? callbackUrl ?? '/'
        : {
            pathname: routes[Actions.SignIn],
            query: {
              error: signIn.error,
              organizationId,
            },
          }

      setStatus(signIn.ok ? 'ok' : 'failed')
      await router.replace(destination)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [])

  return (
    <>
      <Head>
        <title>Sign in</title>
      </Head>

      <DialogTitle>
        <Flex align="center" direction="column" gap="4" justify="center">
          {status === 'loading' && (
            <>
              <Loader /> Signing in
            </>
          )}
          {status === 'ok' && <>Welcome!</>}
          {status === 'failed' && <>Sign in failed</>}
        </Flex>
      </DialogTitle>
    </>
  )
}

/** @type {GetServerSideAuthProps} */
async function getServerSideProps(context, authContext) {
  const [ssoType = 'oidc', organizationId = ''] = authContext.params

  try {
    if (ssoType === 'saml') {
      return await getServerSideSAMLProps(context, authContext)
    }

    if (ssoType === 'oidc') {
      return await getServerSideOIDCProps(context, authContext)
    }

    throw new Error(`Unknown SSO configuration type '${ssoType}'`)
  } catch (error) {
    const destination = authContext.routes[Actions.SignIn].replace(
      '[organizationId]',
      organizationId,
    )

    return {
      redirect: {
        destination: `${destination}?error=${error.message}`,
        permanent: false,
      },
    }
  }
}

/** @type {GetServerSideAuthProps} */
async function getServerSideOIDCProps(context, authContext) {
  const { query } = context
  const [ssoType = 'oidc', organizationIdParam] = authContext.params

  const {
    state,
    cookie,
    redirectURL,
    organizationId: organizationIdState,
  } = await withSecret(context, 'synqly-sso-state', {
    sameSite: 'lax',
    validate: async (stored) => {
      if (
        stored &&
        stored.type === ssoType &&
        query.state === stored?.state &&
        Boolean(organizationIdParam || stored.organizationId)
      ) {
        return stored
      }

      throw new Error('Invalid state', {
        cause: {
          stored,
          query,
          ssoType,
        },
      })
    },
  })

  // Always clear sso state cookie once retrieved
  await withSecret(context, 'synqly-sso-state', {
    validate: () => undefined,
  })

  return {
    props: {
      // Prefer organization ID from URL parameter over stored state, the
      // latter is just for backwards compatibility
      organizationId: organizationIdParam ?? organizationIdState,
      ssoType,
      callbackParams: {
        code: query.code,
        state,
        cookie,
        redirectURL,
      },
    },
  }
}

/** @type {GetServerSideAuthProps} */
async function getServerSideSAMLProps(context, authContext) {
  const [ssoType, organizationId] = authContext.params
  const body = await parseBody(context.req, '1mb')

  if (!body?.RelayState || !body?.SAMLResponse) {
    throw new Error('Invalid state', { cause: body })
  }

  return {
    props: {
      organizationId,
      ssoType,
      callbackParams: {
        relayState: body.RelayState,
        samlResponse: body.SAMLResponse,
      },
    },
  }
}
